import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonRouterLink,
  IonTitle,
  IonToolbar,
  ViewWillEnter,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { WorkPermissionsEnum, WorkProspect, WorkProspectStatusEnum, WorkProspectsV2Service } from '@work-frontend-monorepo/work-rest-api';
import { BadgeComponent, ButtonComponent, IconComponent } from '@work-frontend-monorepo/work-ui/layout';
import { InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { GetFullnameOfContactPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { ProspectStatusBadgePipe } from '../../../../../../../work-office/src/app/features/akquiseobjekte/pipes/prospect-status-badge.pipe';

type AkquiseListeScope = 'mine' | 'all';

interface ProspectsByCity {
  city: string;
  prospects: WorkProspect[];
}

/** Offene und umgewandelte Akquiseobjekte, nach Ort gruppiert — verworfene interessieren unterwegs nicht. */
@Component({
  selector: 'sfg-app-akquise-liste-page',
  templateUrl: './akquise-liste-page.component.html',
  imports: [
    BadgeComponent,
    ButtonComponent,
    GetFullnameOfContactPipe,
    IconComponent,
    InputFieldComponent,
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonRouterLink,
    IonTitle,
    IonToolbar,
    ProspectStatusBadgePipe,
    RouterLink,
    TranslatePipe,
  ],
})
export class AkquiseListePageComponent implements OnInit, ViewWillEnter {
  /** Feuert `ionViewWillEnter` doch beim ersten Eintritt, hat `ngOnInit` gerade eben geladen. */
  private static readonly SKIP_ENTER_AFTER_INIT_MS = 1500;

  private readonly prospectsV2Service = inject(WorkProspectsV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly store = inject(Store);

  private readonly profile = toSignal(this.store.select(ProfileState.profile));
  protected readonly canCreate = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.ProspectCREATE)));

  protected readonly scope = signal<AkquiseListeScope>('mine');
  protected readonly searchTerm = signal('');
  protected readonly loadingState = signal<LoadingAndFeedbackState>('none');
  protected readonly prospects = signal<WorkProspect[]>([]);
  protected readonly hasLoaded = signal(false);

  private initializedAt = 0;

  protected readonly prospectsByCity = computed<ProspectsByCity[]>(() => {
    const groups = new Map<string, WorkProspect[]>();
    for (const prospect of this.prospects()) {
      const city = [prospect.objectZip, prospect.objectCity].filter(Boolean).join(' ') || '–';
      groups.set(city, [...(groups.get(city) ?? []), prospect]);
    }
    return [...groups.entries()].map(([city, prospects]) => ({ city, prospects }));
  });

  /** Ionic feuert `ionViewWillEnter` beim direkten Aufruf nicht verlässlich — der erste Ladevorgang läuft darum hier. */
  async ngOnInit(): Promise<void> {
    this.initializedAt = Date.now();
    await this.load();
  }

  /** Beim Zurückkehren (z. B. nach dem Erfassen) neu laden; direkt nach `ngOnInit` nicht doppelt. */
  async ionViewWillEnter(): Promise<void> {
    if (Date.now() - this.initializedAt < AkquiseListePageComponent.SKIP_ENTER_AFTER_INIT_MS) {
      return;
    }
    await this.load();
  }

  protected async setScope(scope: AkquiseListeScope): Promise<void> {
    this.scope.set(scope);
    await this.load();
  }

  /** Das Eingabefeld entprellt selbst. */
  protected async onSearchChange(searchTerm: string): Promise<void> {
    this.searchTerm.set(searchTerm);
    await this.load();
  }

  private async load(): Promise<void> {
    const profileId = this.profile()?.id;
    this.loadingState.set('loading');
    try {
      const paginated = await lastValueFrom(
        this.prospectsV2Service.prospectsV2ControllerFindAllPaginated({
          limit: 200,
          search: this.searchTerm().trim() || undefined,
          sortBy: ['objectCity:ASC', 'objectStreet:ASC'],
          'filter.active': ['$eq:1'],
          'filter.status': [`$in:${WorkProspectStatusEnum.Open},${WorkProspectStatusEnum.Converted}`],
          'filter.assigneeId': this.scope() === 'mine' && profileId ? [`$eq:${profileId}`] : undefined,
        }),
      );
      this.prospects.set(paginated.data ?? []);
    } catch (loadProspectsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProspectsException', loadProspectsException);
    }
    this.hasLoaded.set(true);
    this.loadingState.set('none');
  }
}
